/** @jsxImportSource @emotion/react */

import { useEffect, useState } from "react";
import {
  css,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BaseButton from "components/common/Button/Base";
import ConfirmDialog from "components/common/Dialog/Confirm";
import { PALETTE } from "styles/base/variables";
import {
  ConfirmRequestStatus,
  PayeeType,
  PayrollConfirmRequestView
} from "models/payrollManagement.model";
import {
  deletePayrollSimpleClosing,
  replyPayrollConfirmRequest
} from "pages/remotes";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  // §2.17 목록 (부모가 조회해서 내려준다). null = 로딩 전/실패
  requests: PayrollConfirmRequestView[] | null;
  // 그리드 칸에서 열면 그 거래처만 보여준다. null = 전체
  businessId?: string | null;
  // 회신·마감 해제 직후 부모가 목록을 다시 조회하도록 (그리드는 닫을 때 한 번만 갱신)
  onChanged: () => void;
  // 마감을 풀었으면 그리드 입력·마감 칸이 바뀌므로 닫을 때 그리드도 다시 조회하게 알린다.
  onClosingReleased: () => void;
}

const PAYEE_TYPE_LABEL: Record<PayeeType, string> = {
  EMPLOYEE: "근로",
  DAILY_WORKER: "일용",
  BUSINESS_INCOME: "사업",
  OTHER_INCOME: "기타"
};

const REPLY_MAX = 500;

const fmtDateTime = (s: string | null | undefined) =>
  s ? s.slice(0, 16).replace("T", " ") : "";

// YYYYMM → YYYY.MM
const fmtYm = (s: string | null | undefined) =>
  s && s.length === 6 ? `${s.slice(0, 4)}.${s.slice(4)}` : "-";

function ConfirmRequestDialog({
  isOpen,
  onClose,
  requests,
  businessId = null,
  onChanged,
  onClosingReleased
}: Props) {
  const [tab, setTab] = useState<ConfirmRequestStatus>("PENDING");
  const [bizFilter, setBizFilter] = useState<string | null>(businessId);
  // 회신 입력 중인 요청 id → 입력값
  const [drafts, setDrafts] = useState<Map<string, string>>(new Map());
  const [busy, setBusy] = useState<Set<string>>(new Set());
  const [reopenTarget, setReopenTarget] =
    useState<PayrollConfirmRequestView | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setTab("PENDING");
    setBizFilter(businessId);
    setDrafts(new Map());
  }, [isOpen, businessId]);

  const filtered = (requests ?? []).filter(
    (v) => !bizFilter || v.businessId === bizFilter
  );
  // 대기 탭: 신고월 오름차순(기한 급한 것 먼저) → 같은 신고월은 먼저 온 요청부터.
  // 신고월이 없는 건(신고주기 NONE)은 맨 뒤.
  const pendingList = filtered
    .filter((v) => v.status === "PENDING")
    .sort(
      (a, b) =>
        (a.filingMonth ?? "999999").localeCompare(b.filingMonth ?? "999999") ||
        a.requestedAt.localeCompare(b.requestedAt)
    );
  const answeredList = filtered.filter((v) => v.status === "ANSWERED");
  const filterName =
    bizFilter &&
    (requests ?? []).find((v) => v.businessId === bizFilter)?.businessName;
  const list = tab === "PENDING" ? pendingList : answeredList;

  const setDraft = (id: string, value: string | null) =>
    setDrafts((prev) => {
      const next = new Map(prev);
      if (value === null) next.delete(id);
      else next.set(id, value);
      return next;
    });

  const run = (id: string, job: () => Promise<any>, errMsg: string) => {
    if (busy.has(id)) return Promise.resolve(false);
    setBusy((prev) => new Set(prev).add(id));
    return job()
      .then(() => {
        onChanged();
        return true;
      })
      .catch((err) => {
        console.error(err);
        alert(err.response?.data?.message || errMsg);
        return false;
      })
      .finally(() =>
        setBusy((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        })
      );
  };

  // 회신 내용을 비워 보내면 회신 내용 없이 처리완료 (payroll-input-api 2.9)
  const submitReply = (v: PayrollConfirmRequestView) => {
    const reply = (drafts.get(v.id) ?? "").trim();
    run(
      v.id,
      () =>
        replyPayrollConfirmRequest(v.id, {
          businessId: v.businessId,
          reply: reply || null
        }),
      "회신 처리 중 오류가 발생했습니다."
    ).then((ok) => ok && setDraft(v.id, null));
  };

  const releaseClosing = () => {
    const v = reopenTarget;
    if (!v) return;
    run(
      v.id,
      () => deletePayrollSimpleClosing(v.businessId, v.accrualMonth),
      "마감 해제 중 오류가 발생했습니다."
    ).then((ok) => {
      if (ok) {
        onClosingReleased();
        setDraft(v.id, null);
      }
      setReopenTarget(null);
    });
  };

  const thCss = css`
    text-align: left;
    font-size: 11px;
    font-weight: 700;
    color: ${PALETTE.textMid};
    padding: 0 10px 6px 0;
    border-bottom: 1px solid ${PALETTE.borderLight};
    white-space: nowrap;
  `;
  const tdCss = css`
    padding: 9px 10px 9px 0;
    border-bottom: 1px solid ${PALETTE.borderLight};
    vertical-align: top;
    font-size: 13px;
    line-height: 1.5;
  `;
  const subCss = css`
    display: block;
    font-size: 11px;
    color: ${PALETTE.textMid};
  `;
  const smallBtnCss = css`
    padding: 4px 10px;
    font-size: 12px;
    white-space: nowrap;
  `;

  const targetCell = (v: PayrollConfirmRequestView) =>
    v.kind === "REOPEN" ? (
      <td css={tdCss}>
        <b
          css={css`
            color: ${PALETTE.error};
          `}
        >
          마감 해제 요청
        </b>
      </td>
    ) : (
      <td css={tdCss}>
        {v.payeeName || "-"}
        {v.payeeType && (
          <span css={subCss}>{PAYEE_TYPE_LABEL[v.payeeType]}</span>
        )}
      </td>
    );

  const reasonCell = (v: PayrollConfirmRequestView) => (
    <td css={tdCss}>
      {v.autoReasons.length > 0 && (
        <ul
          css={css`
            margin: 0 0 2px;
            padding-left: 16px;
          `}
        >
          {v.autoReasons.map((r, i) => (
            <li key={i}>{r}</li>
          ))}
        </ul>
      )}
      {v.memo ? (
        <span
          css={css`
            white-space: pre-wrap;
            color: ${PALETTE.text};
          `}
        >
          “{v.memo}”
        </span>
      ) : (
        !v.autoReasons.length && (
          <span
            css={css`
              color: ${PALETTE.textMid};
            `}
          >
            -
          </span>
        )
      )}
    </td>
  );

  const replyEditor = (v: PayrollConfirmRequestView) => (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 4px;
        min-width: 240px;
      `}
    >
      <textarea
        value={drafts.get(v.id) ?? ""}
        maxLength={REPLY_MAX}
        placeholder="회신 내용 (비워 두면 내용 없이 처리완료)"
        disabled={busy.has(v.id)}
        onChange={(e) => setDraft(v.id, e.target.value)}
        css={css`
          font: inherit;
          font-size: 12px;
          resize: vertical;
          min-height: 44px;
          padding: 6px 8px;
          border: 1px solid ${PALETTE.borderLight};
          border-radius: 4px;
        `}
      />
      <div
        css={css`
          display: flex;
          gap: 4px;
          justify-content: flex-end;
        `}
      >
        {v.status === "ANSWERED" && (
          <BaseButton
            variant="outlined"
            color={PALETTE.textMid}
            disabled={busy.has(v.id)}
            onClick={() => setDraft(v.id, null)}
            css={smallBtnCss}
          >
            취소
          </BaseButton>
        )}
        {v.kind === "REOPEN" && v.status === "PENDING" && (
          <BaseButton
            variant="outlined"
            color={PALETTE.error}
            disabled={busy.has(v.id)}
            onClick={() => setReopenTarget(v)}
            css={smallBtnCss}
          >
            마감 해제
          </BaseButton>
        )}
        <BaseButton
          variant="contained"
          color={PALETTE.primary}
          disabled={busy.has(v.id)}
          onClick={() => submitReply(v)}
          css={smallBtnCss}
        >
          {v.status === "ANSWERED"
            ? "수정"
            : (drafts.get(v.id) ?? "").trim()
            ? "회신"
            : "처리완료"}
        </BaseButton>
      </div>
    </div>
  );

  const answerCell = (v: PayrollConfirmRequestView) => {
    if (v.status === "PENDING")
      return <td css={tdCss}>{replyEditor(v)}</td>;
    if (drafts.has(v.id)) return <td css={tdCss}>{replyEditor(v)}</td>;
    return (
      <td css={tdCss}>
        <span
          css={css`
            white-space: pre-wrap;
            color: ${v.reply ? PALETTE.text : PALETTE.textMid};
          `}
        >
          {v.reply || "(회신 내용 없이 처리완료)"}
        </span>
        <span css={subCss}>
          {fmtDateTime(v.repliedAt)}{" "}
          {v.repliedByName || v.repliedByUser || ""}
          {" · "}
          <button
            type="button"
            onClick={() => setDraft(v.id, v.reply ?? "")}
            css={css`
              border: 0;
              background: transparent;
              font: inherit;
              color: ${PALETTE.primary};
              cursor: pointer;
              padding: 0;
              text-decoration: underline;
            `}
          >
            회신 수정
          </button>
        </span>
      </td>
    );
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        css={css`
          font-size: 16px;
          font-weight: 700;
        `}
      >
        급여 확인요청
        <span
          css={css`
            font-weight: 400;
            font-size: 12px;
            color: ${PALETTE.textMid};
            margin-left: 8px;
          `}
        >
          거래처가 세특 [급여입력]에서 보낸 확인요청과 마감 해제 요청입니다
        </span>
        <IconButton
          onClick={onClose}
          css={css`
            position: absolute;
            right: 8px;
            top: 8px;
          `}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 6px;
            border-bottom: 1px solid ${PALETTE.borderLight};
            margin-bottom: 12px;
          `}
        >
          {(
            [
              ["PENDING", `대기 ${pendingList.length}`],
              ["ANSWERED", `처리완료 ${answeredList.length}`]
            ] as [ConfirmRequestStatus, string][]
          ).map(([t, label]) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              css={css`
                border: 0;
                background: transparent;
                font: inherit;
                font-size: 13px;
                font-weight: 600;
                cursor: pointer;
                padding: 7px 12px 9px;
                color: ${tab === t ? PALETTE.text : PALETTE.textMid};
                border-bottom: 2px solid
                  ${tab === t ? PALETTE.primary : "transparent"};
              `}
            >
              {label}
            </button>
          ))}
          {bizFilter && (
            <span
              css={css`
                margin-left: auto;
                font-size: 12px;
                color: ${PALETTE.textMid};
              `}
            >
              <b
                css={css`
                  color: ${PALETTE.text};
                `}
              >
                {filterName || "선택한 거래처"}
              </b>
              만 보는 중 ·{" "}
              <button
                type="button"
                onClick={() => setBizFilter(null)}
                css={css`
                  border: 0;
                  background: transparent;
                  font: inherit;
                  color: ${PALETTE.primary};
                  cursor: pointer;
                  padding: 0;
                  text-decoration: underline;
                `}
              >
                전체 보기
              </button>
            </span>
          )}
        </div>

        <p
          css={css`
            margin: 0 0 8px;
            font-size: 12px;
            color: ${PALETTE.textMid};
          `}
        >
          {tab === "PENDING" ? (
            <>
              대기 중인 요청은 달과 관계없이 모두 보입니다. 회신 내용을 비워
              두고 <b>처리완료</b>를 누르면 내용 없이 처리됩니다. 마감 해제
              요청은 <b>마감 해제</b>를 누르면 그 귀속월 급여 마감이 모두 풀리고
              요청도 처리완료됩니다. 처리완료한 요청은 대기로 되돌릴 수
              없습니다.
            </>
          ) : (
            <>이번 지급월에 담기는 귀속월의 처리완료 요청입니다.</>
          )}
        </p>

        <table
          css={css`
            width: 100%;
            border-collapse: collapse;
          `}
        >
          <thead>
            <tr>
              <th css={thCss}>거래처</th>
              <th css={thCss}>대상</th>
              <th css={thCss}>귀속월</th>
              <th css={thCss}>신고월</th>
              <th css={thCss}>요청 사유</th>
              <th css={thCss}>요청</th>
              <th css={thCss}>{tab === "PENDING" ? "회신" : "회신 내용"}</th>
            </tr>
          </thead>
          <tbody>
            {!requests ? (
              <tr>
                <td css={tdCss} colSpan={7} style={{ color: PALETTE.textMid }}>
                  불러오는 중입니다.
                </td>
              </tr>
            ) : !list.length ? (
              <tr>
                <td css={tdCss} colSpan={7} style={{ color: PALETTE.textMid }}>
                  {tab === "PENDING"
                    ? "대기 중인 요청이 없습니다."
                    : "처리완료한 요청이 없습니다."}
                </td>
              </tr>
            ) : (
              list.map((v) => (
                <tr key={v.id}>
                  <td css={tdCss}>
                    <b>{v.businessName}</b>
                  </td>
                  {targetCell(v)}
                  <td css={tdCss}>{fmtYm(v.accrualMonth)}</td>
                  <td css={tdCss}>{fmtYm(v.filingMonth)}</td>
                  {reasonCell(v)}
                  <td css={tdCss} style={{ whiteSpace: "nowrap" }}>
                    {fmtDateTime(v.requestedAt)}
                    <span css={subCss}>
                      {v.requestedByName || v.requestedByUser || ""}
                    </span>
                  </td>
                  {answerCell(v)}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DialogContent>

      <ConfirmDialog
        isOpen={reopenTarget !== null}
        title="급여 마감 해제"
        confirmLabel="마감 해제"
        disabled={reopenTarget !== null && busy.has(reopenTarget.id)}
        onCancel={() => setReopenTarget(null)}
        onConfirm={releaseClosing}
      >
        <DialogContentText>
          <b>{reopenTarget?.businessName}</b>의{" "}
          {fmtYm(reopenTarget?.accrualMonth)} 귀속 급여 마감(근로·일용·사업·기타)을
          모두 해제합니다.
          <br />
          마감 해제 요청은 «마감을 해제했습니다»로 자동 회신됩니다.
        </DialogContentText>
      </ConfirmDialog>
    </Dialog>
  );
}

export default ConfirmRequestDialog;
